import { useEffect, useState } from 'react';
import type { RedemptionRecord } from '@sundkortid/shared';
import { api, ApiRequestError } from '../../api/client';

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString('is-IS', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function HistoryPage() {
  const [records, setRecords] = useState<RedemptionRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<RedemptionRecord[]>('/redemptions/history')
      .then(setRecords)
      .catch((err) =>
        setError(err instanceof ApiRequestError ? err.message : 'Villa við að sækja sögu'),
      )
      .finally(() => setIsLoading(false));
  }, []);

  const validCount = records.filter((r) => !r.isReversed).length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-primary-400 mb-2">
            Saga
          </p>
          <h1 className="text-3xl font-title font-bold text-primary-900">Notkun</h1>
        </div>
        {!isLoading && records.length > 0 && (
          <span className="text-sm font-medium text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
            {validCount} heimsóknir
          </span>
        )}
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-gray-100 animate-pulse shrink-0" />
              <div className="flex-1 bg-white rounded-xl border border-gray-100 p-4 h-16 animate-pulse" />
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && records.length === 0 && (
        <div className="text-center py-16">
          <div className="w-20 h-20 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-primary-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
          </div>
          <h3 className="text-lg font-title font-bold text-primary-900 mb-2">Engin notkun</h3>
          <p className="text-sm text-gray-400 max-w-xs mx-auto">
            Engin notkun skráð enn. Notaðu QR kóðann þinn í sundlaug til að sjá sögu hér.
          </p>
        </div>
      )}

      {/* Timeline */}
      {!isLoading && !error && records.length > 0 && (
        <div className="space-y-4 stagger-children">
          {records.map((r) => (
            <div key={r.id} className="relative flex gap-4 timeline-dot">
              {/* Dot */}
              <div
                className={`relative z-10 mt-1 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                  r.isReversed ? 'bg-gray-100' : 'bg-green-50'
                }`}
              >
                {r.isReversed ? (
                  <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                  </svg>
                )}
              </div>

              {/* Card */}
              <div
                className={`flex-1 rounded-xl border p-4 ${
                  r.isReversed
                    ? 'bg-gray-50 border-gray-200'
                    : 'bg-white border-gray-100 shadow-sm'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-semibold text-gray-900 text-sm">{r.poolName}</h4>
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      r.isReversed
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-green-50 text-green-700'
                    }`}
                  >
                    {r.isReversed ? 'Afturkallað' : 'Gilt'}
                  </span>
                </div>
                <p className="text-xs text-gray-400">{formatDate(r.scannedAt)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
